import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { getRuntimeConfig } from "@shared-core/config/runtime-config";
import { formatBytes } from "@shared-core/domain/formatters";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  ARTWORK_CHECK_SCENARIOS,
  sampleFileNameFor,
} from "@shared-core/mock-data/customer-portal-artwork-check";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import { Card } from "../../app/components/primitives/card";
import { Button } from "../../app/components/primitives/button";
import { AlertTriangle, FileText, Loader2, Upload, X } from "lucide-react";
import { useIsReadOnlyView, usePortalViewer } from "./PortalViewerContext";
import {
  ACT_FOR_TO_CHECK_ARTWORK,
  NO_ARTWORK_CHECK_WHEN_LOOKING,
  StaffViewReadOnlyNote,
} from "./StaffViewReadOnlyNote";

// Artwork preflight check journey (upload) — a customer picks a print-ready
// PDF and finds out what's wrong with it in the same sitting, before any
// human here has looked at it. NOT the proof-approval journey (/proofs,
// already built) and NOT the catalogue's artwork upload (apps/frontend, a
// different app and identity model) — this is its own page, its own journey.
//
// PDF only, deliberately: the preflight profile behind this (`CIS Preflight`,
// see PortalArtworkCheckResultPage.tsx's header) is a PDF analysis profile —
// accepting an image would produce a meaningless verdict, not a lenient one.
//
// Wired to the real backend (artwork_preflight_module's
// customer_artwork_check_router, POST /customer/artwork-checks) via
// CustomerDataAdapter.submitArtworkCheck — a real multipart upload, unlike
// every other CustomerDataAdapter method (see apiClient.postForm in
// data/api/client.ts).
//
// THE HONEST STATE TODAY IS THE ERROR STATE: PREPRESS_CALLAS_ENDPOINT is
// unset in every environment, so the backend runs RaisingPreflightClient and
// a real submission always fails with a 502 (customer_artwork_check.py) —
// deliberately, because a mock verdict would tell a real customer their file
// is print-ready when nothing inspected it. See the `submitError` card below
// for how that reads to a customer; it is the primary path this journey
// produces today, not an edge case.

// 500 MB, set by the business owner. Nothing else in the stack states a
// limit — no Caddy request-body cap, nothing in `FileStore`, and
// `upload_catalogue_artwork` only requires a non-empty file — so this is a
// decision rather than a copy of an existing ceiling. Long brochures and
// large-format work genuinely run past 100 MB, and a customer who hits the
// wall gives up rather than re-exporting.
//
// **This is a client-side hint today and the server cannot yet honour it.**
// `upload_catalogue_artwork` takes `artwork_bytes: bytes` — the whole file
// is buffered in the API process — so a 500 MB ceiling costs 500 MB of
// memory per concurrent upload on a box running everything else. Wiring
// this to a real endpoint needs streaming to disk first; raising the number
// here does not on its own make a 500 MB upload safe.
const MAX_FILE_SIZE_BYTES = 500_000_000;

export function validatePdf(file: File): string | null {
  const looksLikePdf =
    file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
  if (!looksLikePdf) {
    return "We can only check PDFs. Please export your artwork as a PDF and try again.";
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `That file is ${formatBytes(file.size)} — we can check files up to ${formatBytes(MAX_FILE_SIZE_BYTES)}. Try flattening or re-exporting at a smaller size.`;
  }
  return null;
}

/** A minimal, valid-looking PDF byte sequence — enough for a `File` with a
 *  real `application/pdf` type and a non-zero size. Only used by the sample
 *  buttons below; a real upload never goes through this. */
function sampleFile(name: string): File {
  const bytes = new TextEncoder().encode("%PDF-1.4\n%%EOF");
  return new File([bytes], name, { type: "application/pdf" });
}

// The specific reason (an ApiError's `detail`, or a plain Error's message)
// is always preferred — see mutationErrorMessage's own docstring — but a
// network drop reaches here with neither, so this is what a customer sees
// when we can't even say that much.
const SUBMIT_FAILURE_FALLBACK =
  "We couldn't reach our file-checking service just now.";

export function PortalArtworkCheckPage() {
  const adapter = useCustomerDataAdapter();
  const navigate = useNavigate();
  const { acting, actingCustomerName, buildPortalUrl, canActFor } =
    usePortalViewer();
  // Staff only looking at the customer's page: the form is drawn as the customer sees it, and does nothing. Whether
  // they can act for the customer is the session's word, so the sentence waits for it (the form stays off meanwhile).
  const isReadOnly = useIsReadOnlyView();
  const inputRef = useRef<HTMLInputElement>(null);

  // "Try an example" is a review/debugging affordance, not part of the real
  // upload (see this file's header) — it fakes a verdict by filename, which
  // only the mock adapter can honour. Against the real backend it would be
  // actively misleading: every one of these tiny fake PDFs would genuinely
  // upload and come back as the same 502 (see the submitError card below),
  // not the scenario its label promises. Hidden whenever a real API base URL
  // is configured; shown in mock mode (including Storybook, which loads no
  // config.json — see .storybook/preview.tsx) so a reviewer can still see
  // every verdict shape even though a real upload only ever errors today.
  const isMockMode = !getRuntimeConfig().apiBaseUrl;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Act-for confirm step (amendment rule 5: "Any action that submits
  // outward... has a confirm step naming the customer"). Only ever true
  // when `acting` is true — see the "Check this file" button below, which
  // routes the first click here instead of straight to `submit()`.
  const [awaitingActForConfirm, setAwaitingActForConfirm] = useState(false);

  function selectFile(file: File | null) {
    setSubmitError(null);
    setAwaitingActForConfirm(false);
    if (!file) {
      setSelectedFile(null);
      setFileError(null);
      return;
    }
    const error = validatePdf(file);
    if (error) {
      setSelectedFile(null);
      setFileError(error);
      return;
    }
    setFileError(null);
    setSelectedFile(file);
  }

  async function submit() {
    if (!selectedFile) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const check = await adapter.submitArtworkCheck(selectedFile);
      navigate(
        buildPortalUrl(
          paths.portalArtworkCheckResult({ checkId: check.check_id }),
        ),
      );
    } catch (err) {
      setSubmitting(false);
      setSubmitError(mutationErrorMessage(err, SUBMIT_FAILURE_FALLBACK));
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">
          Check your artwork
        </h1>
        <p className="text-sm text-muted-foreground">
          Upload a print-ready PDF and we'll tell you straight away if anything
          needs fixing before it goes to print.
        </p>
      </div>

      {isReadOnly && canActFor !== null && (
        <StaffViewReadOnlyNote>
          {canActFor ? ACT_FOR_TO_CHECK_ARTWORK : NO_ARTWORK_CHECK_WHEN_LOOKING}
        </StaffViewReadOnlyNote>
      )}

      <Card className="space-y-4 p-5">
        <div className="rounded-lg border border-dashed border-input p-6 text-center">
          <Upload className="mx-auto size-6 text-muted-foreground" />
          <p className="mt-2 text-sm font-medium">Choose a PDF to check</p>
          <p className="text-xs text-muted-foreground">
            PDF only, up to {formatBytes(MAX_FILE_SIZE_BYTES)}.
          </p>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="sr-only"
            aria-label="Choose a PDF to check"
            disabled={isReadOnly}
            onChange={(e) => selectFile(e.target.files?.[0] ?? null)}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => inputRef.current?.click()}
            disabled={submitting || isReadOnly}
          >
            <Upload className="size-4" />
            Choose file
          </Button>
        </div>

        {fileError && (
          <p role="alert" className="text-sm text-red-700 dark:text-red-400">
            {fileError}
          </p>
        )}

        {selectedFile && (
          <div className="flex items-center justify-between gap-3 rounded-md border border-muted bg-muted/30 p-3">
            <div className="flex min-w-0 items-center gap-2">
              <FileText className="size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatBytes(selectedFile.size)}
                </p>
              </div>
            </div>
            {!submitting && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Remove file"
                onClick={() => {
                  selectFile(null);
                  if (inputRef.current) inputRef.current.value = "";
                }}
              >
                <X className="size-4" />
              </Button>
            )}
          </div>
        )}

        {/* The state a customer hits first, today: PREPRESS_CALLAS_ENDPOINT
            is unset everywhere, so every real submission errors here (see
            this file's header). This must read as "we don't know yet" — not
            as a pass, not as a fail, and not as "this is fine, try later" —
            because nobody and nothing has actually looked at the file. */}
        {submitError && (
          <Card
            role="alert"
            className="flex flex-col items-center gap-2 border-amber-300 bg-amber-50 p-6 text-center dark:border-amber-900 dark:bg-amber-950/20"
          >
            <AlertTriangle className="size-6 text-amber-700 dark:text-amber-400" />
            <p className="font-medium">We couldn&apos;t check this file</p>
            <p className="max-w-sm text-sm text-muted-foreground">
              {submitError}
            </p>
            <p className="max-w-sm text-xs text-muted-foreground">
              Nobody has looked at it, so we can&apos;t tell you either way
              whether it&apos;s ready to print. Try again in a few minutes, or
              get in touch and we&apos;ll check it for you.
            </p>
          </Card>
        )}

        {awaitingActForConfirm ? (
          <Card
            role="alertdialog"
            aria-label="Confirm submitting for the customer"
            className="space-y-3 border-red-300 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/20"
          >
            <p className="text-sm font-medium">
              Submit this artwork check for{" "}
              {actingCustomerName ?? "this customer"}?
            </p>
            <p className="text-xs text-muted-foreground">
              This is a real submission on their behalf — it will appear in
              their portal marked "Submitted for you by Pollards".
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={() => {
                  setAwaitingActForConfirm(false);
                  void submit();
                }}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Checking…
                  </>
                ) : (
                  "Confirm and submit"
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAwaitingActForConfirm(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
            </div>
          </Card>
        ) : (
          <Button
            type="button"
            onClick={() =>
              acting ? setAwaitingActForConfirm(true) : void submit()
            }
            disabled={!selectedFile || submitting || isReadOnly}
          >
            {submitting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Checking your file…
              </>
            ) : (
              "Check this file"
            )}
          </Button>
        )}
      </Card>

      {/* Review-tool affordance, same explicit framing as ViewerMenu's
          persona switcher (PortalLayout.tsx) — not part of the real journey.
          Mock mode only (see isMockMode above): against a real backend a
          "Try an example" button that always actually errors would be a
          confusing fiction, not a useful debugging affordance. */}
      {isMockMode && (
        <Card className="space-y-3 p-5">
          <div>
            <p className="text-sm font-medium">Try an example</p>
            <p className="text-xs text-muted-foreground">
              See how the check responds to different files — for review only,
              not part of the real upload.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {ARTWORK_CHECK_SCENARIOS.map((scenario) => (
              <Button
                key={scenario.id}
                type="button"
                variant="outline"
                size="sm"
                disabled={submitting || isReadOnly}
                onClick={() =>
                  selectFile(sampleFile(sampleFileNameFor(scenario.id)))
                }
              >
                {scenario.label}
              </Button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
